import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listPayments, type Payment } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";

export function PaymentsPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [ticket, setTicket] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listPayments({
        limit: 100,
        status: status || undefined,
        payment_method: method || undefined,
        ticket_number: ticket.trim() || undefined,
      });
      setPayments(resp.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method]);

  const columns: ColumnDef<Payment>[] = [
    {
      key: "id",
      header: "ID",
      minWidth: 280,
      cell: (r) => <span className="font-mono text-xs text-[#7e8299]">{r.id}</span>,
    },
    {
      key: "reference",
      header: "Référence / Billet",
      minWidth: 220,
      accessor: (r) => r.external_reference,
      sortable: true,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="font-mono text-sm text-[#181c32]">{r.external_reference}</div>
          {r.parsed_metadata && (r.parsed_metadata as Record<string, string>).ticket_number && (
            <div className="text-xs text-brand-600 font-semibold mt-0.5">
              Billet: {(r.parsed_metadata as Record<string, string>).ticket_number}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 100,
      sortable: true,
      accessor: (r) => r.amount_minor,
      cell: (r) => <span className="font-semibold text-[#181c32]">{formatAmount(r.amount_minor, r.currency).replace(` ${r.currency}`, "")}</span>,
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 80,
      sortable: true,
      accessor: (r) => r.currency,
      cell: (r) => <span className="text-sm font-semibold text-[#3f4254]">{r.currency}</span>,
    },
    {
      key: "method",
      header: "Méthode",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.payment_method,
      cell: (r) => <Badge color={methodColor(r.payment_method)}>{r.payment_method}</Badge>,
    },
    {
      key: "env",
      header: "Env.",
      minWidth: 80,
      sortable: true,
      accessor: (r) => (r.livemode ? "live" : "test"),
      cell: (r) =>
        r.livemode ? (
          <span
            title="Transaction en environnement réel (débit bancaire effectif)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200"
          >
            Réel
          </span>
        ) : (
          <span
            title="Transaction en environnement test (carte virtuelle, aucun débit réel)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
          >
            Test
          </span>
        ),
    },
    {
      key: "customer",
      header: "Client",
      minWidth: 180,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="text-sm font-medium text-[#181c32]">
            {[r.customer_first_name, r.customer_last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div className="text-xs text-[#7e8299]">{r.customer_phone || r.customer_email || "—"}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "description",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (r) => {
        if (!r.failure_message) return <span className="text-xs text-[#a1a5b7]">—</span>;
        const color = r.status === "succeeded" ? "text-success-700" : "text-danger-700";
        return (
          <span className={`text-xs ${color} block max-w-[280px]`} title={r.failure_message}>
            {r.failure_message}
          </span>
        );
      },
    },
    {
      key: "created_at",
      header: "Date",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.created_at,
      cell: (r) => <span className="text-xs text-[#7e8299]">{formatDate(r.created_at)}</span>,
    },
  ];

  const filters = (
    <>
      <div className="relative">
        <input
          value={ticket}
          onChange={(e) => setTicket(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Numéro de billet (ex: AB1234)"
          className="form-input py-2 px-3 text-sm w-56 uppercase"
        />
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous statuts</option>
        <option value="succeeded">Succeeded</option>
        <option value="processing">Processing</option>
        <option value="pending">Pending</option>
        <option value="failed">Failed</option>
      </select>
      <select value={method} onChange={(e) => setMethod(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Toutes méthodes</option>
        <option value="mpesa">M-Pesa</option>
        <option value="orange">Orange</option>
        <option value="airtel">Airtel</option>
        <option value="afrimoney">AfriMoney</option>
        <option value="visa">Visa</option>
        <option value="mastercard">Mastercard</option>
      </select>
      <button onClick={load} className="btn btn-primary btn-sm">Filtrer</button>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Paiements"
        breadcrumbs={[{ label: "Plateforme" }, { label: "Paiements" }]}
      />

      <DataTable<Payment>
        title="Transactions"
        subtitle="Recherche par numéro de billet, statut, ou méthode"
        searchable
        searchPlaceholder="Rechercher dans les résultats…"
        columns={columns}
        data={payments}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/payments/${r.id}`)}
        loading={loading}
        filtersSlot={filters}
        emptyMessage="Aucun paiement trouvé"
      />
    </div>
  );
}

function methodColor(method: string): "primary" | "info" | "warning" | "light" {
  if (method === "mpesa" || method === "afrimoney") return "primary";
  if (method === "orange") return "warning";
  if (method === "airtel") return "info";
  return "light";
}
